import { indentWithTab } from '@codemirror/commands';
import { java } from '@codemirror/lang-java';
import { EditorView, keymap } from '@codemirror/view';
import { basicSetup } from 'codemirror';
import n2w from 'number-to-words';
import prettier from 'prettier/standalone';
import prettierPluginJava from 'prettier-plugin-java';
import { confirmButton } from './modules/confirm-button.ts';
import { $, a, must, text } from './modules/dom.ts';
import type { SiteMetadata } from './modules/evaluator-types.ts';
import {
  bootstrapFailed,
  describe,
  errorDetail,
  getJson,
  pageError,
} from './modules/fetch-helpers.ts';
import Login, { showToolbarUser } from './modules/login.ts';
import {
  type AnswerValue,
  type AuthInfo,
  type ChoiceAnswer,
  type KeyEntry,
  QuizAnswerStore,
  type SaveState,
} from './modules/quiz-answers.ts';
import { shuffled } from './modules/shuffle.ts';
import type { Storage } from './modules/storage.ts';
import { typesetMath } from './modules/typeset-math.ts';

// Legacy (baked) quizzes: answers.json is a positional array — a string per
// question, or an array of strings for multiple-choice — and key.json a
// positional array of entries.
type LegacyAnswer = string | string[];
type LegacyKeyEntry = { answer: LegacyAnswer; explanation?: string };

const javaPrettierOpts = { parser: 'java', plugins: [prettierPluginJava] };

// Prettier 3's format() is async: without the awaits the catches could never
// see its (rejected-promise) failures, so the fallbacks never ran. Returns
// null when the code can't be parsed (so the caller can say so) — the
// parse errors themselves deliberately stay unreported since on quizzes
// about syntax errors the line number would give away the answer.
const formatJavaCode = async (code: string): Promise<string | null> => {
  try {
    return await prettier.format(code, javaPrettierOpts);
  } catch {
    // Try one more thing before we give up
    try {
      return declassify(await prettier.format(`class X {\n${code}\n}`, javaPrettierOpts));
    } catch {
      return null;
    }
  }
};

// FIXME: just need to figure out a way to know what language we're using and
// then dispatch to the right formatting function. Could probably arrange to
// stick it in a data attribute of the body or something.
const formatCode = formatJavaCode;

const declassify = (text: string) => {
  return text
    .trim()
    .split('\n')
    .slice(2, -1)
    .map((x) => x.slice(2))
    .join('\n');
};

const instructions = $('#instructions');
const hidden = $('#hidden');
const questionsElement = $('#questions');

// Single-page quizzes (quiz layout="single-page") carry this class; they show
// every question at once and drop the one-at-a-time pager (see setup()).
const singlePage = questionsElement.classList.contains('single-page');

/*
 * Dynamically-rendered quiz pages carry data-quiz-* attributes on #questions
 * and store answers on the bhs-cs server (see modules/quiz-answers.ts). On
 * those pages the server has already deterministically shuffled choice order
 * (per student — another student's page may order the same choices
 * differently) and each question div carries data-question-uuid and
 * data-form, with each choice carrying data-choice-index (its display
 * position — the server translates it to the authored entry when the answer
 * is saved, so the index deliberately says nothing about correctness).
 *
 * Legacy baked pages have none of those attributes and keep the original
 * behavior: client-side shuffling and answers.json saved through
 * login.makeStorage().
 */
const serverQuiz = questionsElement.dataset.quizName
  ? {
      course: must(questionsElement.dataset.quizCourse, 'data-quiz-course on #questions'),
      kind: must(questionsElement.dataset.quizKind, 'data-quiz-kind on #questions'),
      name: questionsElement.dataset.quizName,
    }
  : null;

/*
 * The teacher-only /m/ material preview (modules/quiz-preview.ts) renders
 * this same page with data-preview on #questions instead of the data-quiz-*
 * attributes: the quiz looks and behaves exactly as assigned, but nothing
 * is saved anywhere, and the answer key is embedded in the page (in the
 * server-mode uuid-keyed shape) so "Show answers" is always available.
 */
const preview = questionsElement.dataset.preview !== undefined;

let current = window.location.hash ? Number.parseInt(window.location.hash.substring(1), 10) - 1 : 0;

const EDITOR = Symbol('editor');
const EMPTY_CODE = '\n\n\n\n\n\n';

// The codeanswer widgets stash their CodeMirror instance on the widget element.
type EditorElement = Element & { [EDITOR]?: EditorView };

let questions: HTMLCollectionOf<HTMLElement>;
let answers: LegacyAnswer[] = []; // legacy mode: positional answers.json contents
// legacy: positional array; server: uuid-keyed object
let key: LegacyKeyEntry[] | Record<string, KeyEntry> | null = null;
let storage: Storage | null = null; // legacy mode: storage-layer-backed answers.json
// Legacy mode: the saved answers.json couldn't be loaded (for a reason
// other than "none yet"). Saving is blocked — a save would clobber the
// server's answers with a sparse array — and the page says so.
let legacyLoadFailed = false;
let store: QuizAnswerStore | null = null; // server mode

const capitalize = (s: string) => s[0].toUpperCase() + s.slice(1);

const setup = async () => {
  $('#close').onclick = hideInstructions;
  $('#open').onclick = showInstructions;
  // The pager nav (#previous-q/#next-q, #q/#of) only exists in paged mode; in
  // single-page mode the layout omits it, so don't wire or write to it.
  if (!singlePage) {
    $('#previous-q').onclick = previous;
    $('#next-q').onclick = next;
  }

  questions = questionsElement.children as HTMLCollectionOf<HTMLElement>;
  document.querySelectorAll('.numquestions').forEach((e) => (e.innerHTML = `${questions.length}`));
  document
    .querySelectorAll('.numquestionswords')
    .forEach((e) => (e.innerHTML = `${capitalize(n2w.toWords(questions.length))}`));
  if (!singlePage) $('#of').innerHTML = `${questions.length}`;
  makeChoices(questions);
  makeTrueFalse(questions);
  makeMultipleChoices(questions);
  makeFreeanswers(questions);
  makeLongFreeanswers(questions);
  makeMultianswers(questions);
  makeCodeanswers(questions);
  makeRanges(questions);

  if (preview) {
    await setupPreview();
  } else if (serverQuiz) {
    await setupServerStorage(serverQuiz);
  } else {
    await setupLegacyStorage();
  }

  // Paged mode reveals one question (show() sets inline display); single-page
  // leaves them all visible (CSS #questions.single-page > div { display: block }).
  if (!singlePage) show(current);
};

//
// Legacy storage: answers.json in the assignment's directory (via the
// storage layer) and a publicly-fetchable key.json sidecar.
//

const setupLegacyStorage = async () => {
  const metadata = await getJson<SiteMetadata>('/metadata.json');
  const login = new Login(metadata);

  const s = await login.makeStorage();
  storage = s;

  if (s.saves) {
    const answers = await loadAnswers(s, questions);
    await loadKey(s, answers);
  }
};

const loadAnswers = async (storage: Storage, questions: HTMLCollectionOf<HTMLElement>) => {
  try {
    const text = await storage.load('answers.json');
    answers = JSON.parse(text) as LegacyAnswer[];
    fillAnswers(questions, answers);
    return answers;
  } catch (e) {
    // Only a 404 means "no answers yet — fresh start". Anything else
    // (offline, 5xx, a bad answers.json) must not proceed into saving a
    // sparse array over the real answers: block saving, say so, and make
    // the widgets inert so nobody types into a quiz that isn't recording.
    if ((e as { status?: number }).status === 404) return;
    console.error('Loading saved answers failed:', e);
    legacyLoadFailed = true;
    questionsElement.inert = true;
    showQuizNotice(
      `Couldn't load your saved answers (${errorDetail(e)}) — answers can't be saved. Reload to try again.`,
      false,
    );
  }
};

const fillAnswers = (questions: HTMLCollectionOf<HTMLElement>, answers: LegacyAnswer[]) => {
  answers.forEach((a, i) => {
    fillAnswer(questions[i], a);
  });
};

const fillAnswer = (question: HTMLElement, answer: LegacyAnswer) => {
  fillChoices(question, answer);
  fillTrueFalse(question, answer);
  fillMultipleChoices(question, answer);
  fillFreeanswer(question, answer);
  fillLongFreeanswer(question, answer);
  fillCodeanswer(question, answer);
  fillRange(question, answer);
};

const fillChoices = (question: HTMLElement, answer: LegacyAnswer) => {
  const choices = question.querySelector('.choices, .ochoices');
  if (choices) {
    ([...choices.children] as HTMLElement[]).forEach((d) => {
      if (d.innerText === answer) {
        must(d.querySelector('input'), 'radio in choice').checked = true;
      }
    });
  }
};

const fillTrueFalse = (question: HTMLElement, answer: LegacyAnswer) => {
  const tf = question.querySelector('.tf');
  if (tf) {
    ([...tf.children] as HTMLElement[]).forEach((d) => {
      if (d.innerText === answer) {
        must(d.querySelector('input'), 'radio in true/false choice').checked = true;
      }
    });
  }
};

const fillMultipleChoices = (question: HTMLElement, answer: LegacyAnswer) => {
  const mchoices = question.querySelector('.mchoices, .omchoices');
  if (mchoices) {
    ([...mchoices.children] as HTMLElement[]).forEach((d) => {
      if (answer.includes(d.innerText)) {
        must(d.querySelector('input'), 'checkbox in choice').checked = true;
      }
    });
  }
};

// The freeanswer/longfreeanswer/codeanswer answers are strings in both legacy
// and server shapes; the wider parameter type is because legacy fillAnswer
// blindly offers each question's answer to every widget filler.
const fillFreeanswer = (question: HTMLElement, answer: LegacyAnswer | AnswerValue) => {
  const freeanswer = question.querySelector('.freeanswer');
  if (freeanswer) {
    const input = must(freeanswer.querySelector('input'), 'input in freeanswer');
    const saveNotice = freeanswer.querySelector('.save-notice') as HTMLElement;
    saveNotice.innerText = 'Saved';
    input.value = answer as string;
  }
};

const fillLongFreeanswer = (question: HTMLElement, answer: LegacyAnswer | AnswerValue) => {
  const e = question.querySelector('.longfreeanswer');
  if (e) {
    const input = must(e.querySelector('textarea'), 'textarea in longfreeanswer');
    const saveNotice = e.querySelector('.save-notice') as HTMLElement;
    saveNotice.innerText = 'Saved';
    input.value = answer as string;
  }
};

// A saved multi-answer list (plans/done/multi-answer-questions.md) rebuilds the
// widget's list. A stored STRING — a question switched to the form after
// students had saved a free answer — restores as a one-item list, matching
// how the scoring views read it.
const fillMultianswer = (question: HTMLElement, answer: LegacyAnswer | AnswerValue) => {
  const e = question.querySelector('.multianswer') as HTMLElement | null;
  if (!e) return;
  const items = Array.isArray(answer)
    ? (answer as unknown[]).filter((s): s is string => typeof s === 'string')
    : typeof answer === 'string' && answer.trim()
      ? [answer]
      : [];
  const list = must(e.querySelector('.multianswer-list'), 'list in multianswer');
  // Newest on top: the stored array is in the order written.
  list.replaceChildren(...items.map((s) => multianswerItem(question, s)).reverse());
  multianswerRefreshCount(question);
  const saveNotice = e.querySelector('.save-notice') as HTMLElement | null;
  if (saveNotice) saveNotice.innerText = 'Saved';
};

const fillRange = (question: HTMLElement, answer: LegacyAnswer | AnswerValue) => {
  const e = question.querySelector('.range');
  if (e) {
    const input = must(e.querySelector('input'), 'input in range');
    input.value = answer as string;
    const value = e.querySelector('.range-value') as HTMLElement | null;
    if (value) value.innerText = input.value;
    const saveNotice = e.querySelector('.save-notice') as HTMLElement | null;
    if (saveNotice) saveNotice.innerText = 'Saved';
  }
};

const fillCodeanswer = (question: HTMLElement, answer: LegacyAnswer | AnswerValue) => {
  const e = question.querySelector('.codeanswer');
  if (e) {
    const editor = must((e as EditorElement)[EDITOR], 'editor on codeanswer widget');
    editor.dispatch({
      changes: { from: 0, to: editor.state.doc.length, insert: (answer as string) || EMPTY_CODE },
    });
    (e.querySelector('.save-notice') as HTMLElement).innerText = 'Saved';
  }
};

//
// Server storage: answers saved per-question to the bhs-cs server, keyed by
// question uuid, with the key released by server policy.
//

const setupServerStorage = async (quiz: { course: string; kind: string; name: string }) => {
  store = new QuizAnswerStore(quiz.course, quiz.kind, quiz.name, {
    onSaveState: showSaveState,
    onAuthLost: showLoginNotice,
    onLoadError: (detail) =>
      showQuizNotice(
        `Couldn't load your earlier answers (${detail}). New answers are still being saved; reload to try again.`,
        false,
      ),
  });

  // init() flushes saves buffered by a previous visit, then loads our saved
  // answers (buffered local values win, so nothing appears lost offline).
  const auth = await store.init();
  fillServerAnswers();

  if (auth === 'ok') {
    wasAuthed = true;
    await showToolbarUser();
    await maybeLoadServerKey();
  } else {
    showLoginNotice(auth);
  }
};

/*
 * Preview mode: no storage at all — just say so (the same banner spot the
 * other non-saving states use), and wire "Show answers" to the embedded key.
 * A keyless quiz (a pure feedback form) has no embedded key and the toggle
 * stays hidden, same as everywhere else.
 */
const setupPreview = async () => {
  showQuizNotice('Material preview — answers are not saved.', false);
  await showToolbarUser();
  const embedded = document.getElementById('preview-key');
  if (embedded?.textContent) {
    key = JSON.parse(embedded.textContent) as Record<string, KeyEntry>;
    revealShowAnswersToggle();
  }
};

const questionByUuid = (uuid: string) =>
  ([...questions] as HTMLElement[]).find((q) => q.dataset.questionUuid === uuid);

const SAVE_STATE_TEXT: Record<SaveState, string> = {
  saving: 'Saving…',
  saved: 'Saved',
  offline: 'Offline',
  closed: 'Not saved',
  unsaved: 'Not saved',
  error: 'Not saved — will keep trying',
};

const showSaveState = (uuid: string, state: SaveState, _savedAt?: string, detail?: string) => {
  const question = questionByUuid(uuid);
  if (question) {
    const notice = saveNoticeFor(question);
    notice.innerText = SAVE_STATE_TEXT[state];
    notice.classList.toggle('error', state === 'error');
    notice.title = state === 'error' && detail ? detail : '';
  }
  if (state === 'error') {
    showQuizNotice(
      `The server is refusing to save an answer${detail ? ` (${detail})` : ''}. ` +
        'Your answers are kept in this browser and will be re-sent; tell your teacher.',
      false,
      'save-error',
    );
  }
  if (state === 'saved') {
    // A landed save means the refusal is over: the banner comes down (other
    // notices — logged out, closed, load failure — are not ours to clear).
    hideQuizNotice('save-error');
  }
  if (state === 'closed') {
    showQuizNotice('This quiz is closed.', false);
  }
  if (state === 'saved') {
    maybeLoadServerKey();
  }
};

/*
 * The free and code answer widgets come with a .save-notice; choice widgets
 * don't, so in server mode we add one on demand to show save/retry state.
 */
const saveNoticeFor = (question: HTMLElement): HTMLElement => {
  let notice = question.querySelector<HTMLElement>('.save-notice');
  if (!notice) {
    notice = document.createElement('p');
    notice.classList.add('save-notice');
    question.append(notice);
  }
  return notice;
};

/*
 * A notice above the questions (log in to save, family view, quiz closed).
 * Styled as a sticky banner (form-assessment.css) so it stays visible while
 * the student scrolls the quiz — a mid-quiz logout must not go unnoticed.
 */
const showQuizNotice = (message: string, withLogin: boolean, kind = 'general') => {
  let notice = document.getElementById('quiz-notice');
  if (!notice) {
    notice = document.createElement('p');
    notice.id = 'quiz-notice';
    $('#question-container').prepend(notice);
  }
  notice.dataset.kind = kind;
  notice.replaceChildren(text(message));
  if (withLogin) {
    notice.append(text(' '));
    notice.append(
      a('Log in to save', `/login?from=${encodeURIComponent(window.location.pathname)}`),
    );
  }
};

// Take the banner down, but only if it's still the one `kind` put up.
const hideQuizNotice = (kind: string) => {
  const notice = document.getElementById('quiz-notice');
  if (notice?.dataset.kind === kind) notice.remove();
};

// Did this visit start logged in? Distinguishes "you never logged in" from
// the scarier mid-quiz session loss (server redeploy, expired session).
let wasAuthed = false;

/*
 * Not logged in: the quiz stays fully browsable and the widgets usable, we
 * just don't save anything. Family sessions (read-only views of a student;
 * see server-side modules/family.ts) get their own message without the login
 * link since logging in isn't the answer for them. Same messages and toolbar
 * treatment as login.ts's showNotSaving.
 */
const showLoginNotice = (auth: AuthInfo) => {
  const message = auth.family
    ? "Family view — answers aren't saved."
    : wasAuthed
      ? 'You’ve been logged out — answers are NOT being saved.'
      : 'Nothing is being saved.';
  showQuizNotice(message, !auth.family);
  const button = document.querySelector('#toolbar .buttons .github');
  if (button) {
    const span = document.createElement('span');
    span.className = 'github-user';
    span.append(text(message));
    if (!auth.family) {
      span.append(text(' '));
      span.append(
        a('Log in to save', `/login?from=${encodeURIComponent(window.location.pathname)}`),
      );
    }
    button.replaceWith(span);
  }
};

const fillServerAnswers = () => {
  const s = store;
  if (!s) return;
  ([...questions] as HTMLElement[]).forEach((question) => {
    const uuid = question.dataset.questionUuid;
    if (!uuid) return;
    const answer: AnswerValue | undefined = s.answers[uuid];
    if (answer === undefined) return;
    fillServerAnswer(question, answer);
    if (s.isPending(uuid)) {
      saveNoticeFor(question).innerText = 'Offline';
    } else {
      const notice = question.querySelector<HTMLElement>('.save-notice');
      if (notice) notice.innerText = 'Saved';
    }
  });
};

const fillServerAnswer = (question: HTMLElement, answer: AnswerValue) => {
  // The casts encode which AnswerValue shape each form produces: a single
  // ChoiceAnswer for choices, a ChoiceAnswer[] for mchoices, a string
  // otherwise.
  switch (question.dataset.form) {
    case 'choices':
      checkChoicesByIndex(question, '.choices, .ochoices', answer ? [answer as ChoiceAnswer] : []);
      break;
    case 'mchoices':
      checkChoicesByIndex(question, '.mchoices, .omchoices', (answer ?? []) as ChoiceAnswer[]);
      break;
    case 'tf':
    case 'yn':
      checkTrueFalse(question, answer as string);
      break;
    case 'freeanswer':
      fillFreeanswer(question, answer);
      break;
    case 'longfreeanswer':
      fillLongFreeanswer(question, answer);
      break;
    case 'multianswer':
      fillMultianswer(question, answer);
      break;
    case 'codeanswer':
      fillCodeanswer(question, answer);
      break;
    case 'range':
      fillRange(question, answer);
      break;
  }
};

const checkChoicesByIndex = (question: HTMLElement, selector: string, chosen: ChoiceAnswer[]) => {
  const container = question.querySelector(selector);
  if (!container) return;
  const indexes = new Set(chosen.map((c) => c.index));
  ([...container.children] as HTMLElement[]).forEach((wrapper) => {
    if (indexes.has(Number(wrapper.dataset.choiceIndex))) {
      const input = wrapper.querySelector('input');
      if (input) input.checked = true;
    }
  });
};

const checkTrueFalse = (question: HTMLElement, answer: string) => {
  const container = question.querySelector('.tf, .yn');
  if (!container) return;
  container.querySelectorAll('label').forEach((label) => {
    const span = label.querySelector('span');
    if (span && span.textContent?.trim() === answer) {
      must(label.querySelector('input'), 'radio in true/false label').checked = true;
    }
  });
};

/*
 * Route a changed answer to the right storage. In legacy mode `value` is the
 * answer in its answers.json shape; in server mode the canonical shape for
 * choice forms comes from the widgets' data-choice-index attributes instead.
 */
const answerChanged = (q: number, value: string | string[]) => {
  if (preview) return; // the /m/ preview stores nothing anywhere
  if (serverQuiz) {
    saveServerAnswer(questions[q], value);
  } else {
    saveAnswer(q, value);
  }
};

const saveServerAnswer = (question: HTMLElement, value: string | string[]) => {
  const uuid = question.dataset.questionUuid;
  if (!uuid || !store) return;

  let answer: AnswerValue | undefined;
  if (question.dataset.form === 'choices') {
    answer = checkedChoices(question, '.choices, .ochoices')[0];
  } else if (question.dataset.form === 'mchoices') {
    answer = checkedChoices(question, '.mchoices, .omchoices');
  } else if (question.dataset.form === 'multianswer') {
    // The whole list, on every add, edit or delete.
    answer = value as string[];
  } else {
    // Every other non-choice widget hands us its answer as a string.
    answer = value as string;
  }
  if (answer === undefined) return;

  if (store.save(uuid, answer)) {
    // Immediate feedback; the store updates this as the PUT plays out.
    saveNoticeFor(question).innerText = SAVE_STATE_TEXT.saving;
  } else {
    // Logged out: the answer is buffered locally but can't reach the server.
    // Say so at the question (the banner explains why and links to login).
    saveNoticeFor(question).innerText = SAVE_STATE_TEXT.unsaved;
  }
};

const checkedChoices = (question: HTMLElement, selector: string): ChoiceAnswer[] => {
  const container = question.querySelector(selector);
  if (!container) return [];
  return ([...container.children] as HTMLElement[])
    .filter((w) => w.dataset.choiceIndex !== undefined && w.querySelector('input')?.checked)
    .map((w) => ({ index: Number(w.dataset.choiceIndex), text: w.innerText.trim() }))
    .sort((x, y) => x.index - y.index);
};

/*
 * Ask the server for the key; it is the sole authority on release (P4,
 * plans/quiz-answer-key-release.md). Checked on load and after each save: the
 * server says 403 until its policy is satisfied (self-check: this student has
 * answered everything; released: their class has grades released / the key was
 * manually revealed), in which case we stay hidden and try again after the
 * next save. So a self-check key still appears exactly when the student
 * finishes, while a released key shows immediately regardless of completion.
 */
let keyRequestInFlight = false;

const maybeLoadServerKey = async () => {
  if (!store || key !== null || keyRequestInFlight) return;
  keyRequestInFlight = true;
  try {
    const fetched = await store.fetchKey();
    if (fetched) {
      key = fetched;
      revealShowAnswersToggle();
    }
  } finally {
    keyRequestInFlight = false;
  }
};

const revealShowAnswersToggle = () => {
  const show = $('#show-answers');
  show.style.display = 'inline';
  must(show.querySelector('input'), 'checkbox in #show-answers').onchange = (e) =>
    toggleAnswers(must(key, 'loaded answer key'), (e.target as HTMLInputElement).checked);
};

//
// Widgets.
//

const makeChoices = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div, q) => {
    const choices = div.querySelector('.choices, .ochoices');
    if (choices) {
      ([...choices.children] as HTMLElement[]).forEach((d) => {
        const answer = d.innerText;
        const radio = document.createElement('input');
        const label = document.createElement('label');
        radio.setAttribute('type', 'radio');
        radio.setAttribute('name', `q${q}`);
        radio.onchange = () => answerChanged(q, answer);
        const span = document.createElement('span');
        span.append(d.cloneNode(true));
        label.append(radio);
        label.append(span);
        const wrapper = document.createElement('div');
        if (d.dataset.choiceIndex !== undefined) {
          wrapper.dataset.choiceIndex = d.dataset.choiceIndex;
        }
        wrapper.append(label);
        d.replaceWith(wrapper);
      });
      if (!choices.classList.contains('choices')) {
        choices.classList.add('choices');
      }
      // In server mode (and the preview, which renders the same way) the
      // server has already deterministically shuffled the choices;
      // reshuffling would break the stability of the order — and, in the
      // preview, the key's display indices.
      if (!serverQuiz && !preview && !choices.classList.contains('ochoices')) {
        choices.replaceChildren(...shuffled([...choices.children]));
      }
    }
  });
};

const makeTrueFalse = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div, q) => {
    const choices = div.querySelector('.tf, .yn');
    if (choices) {
      const answers = choices.classList.contains('tf') ? ['True', 'False'] : ['Yes', 'No'];
      answers.forEach((answer) => {
        const p = document.createElement('p');
        const radio = document.createElement('input');
        const label = document.createElement('label');
        radio.setAttribute('type', 'radio');
        radio.setAttribute('name', `q${q}`);
        radio.onchange = () => answerChanged(q, answer);
        const span = document.createElement('span');
        span.append(answer);
        label.append(radio);
        label.append(span);
        p.append(label);
        choices.append(p);
        choices.classList.add('choices');
      });
    }
  });
};

const makeMultipleChoices = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div, q) => {
    const mchoices = div.querySelector('.mchoices, .omchoices');

    if (mchoices) {
      const saveChecked = () => {
        const answers = ([...mchoices.children] as HTMLElement[])
          .filter((c) => must(c.querySelector('input'), 'checkbox in choice').checked)
          .map((c) => c.innerText);
        answerChanged(q, answers);
      };
      ([...mchoices.children] as HTMLElement[]).forEach((d) => {
        const checkbox = document.createElement('input');
        const label = document.createElement('label');
        checkbox.setAttribute('type', 'checkbox');
        checkbox.setAttribute('name', `q${q}`);
        checkbox.onchange = saveChecked;
        const span = document.createElement('span');
        span.append(d.cloneNode(true));
        label.append(checkbox);
        label.append(span);
        const wrapper = document.createElement('div');
        if (d.dataset.choiceIndex !== undefined) {
          wrapper.dataset.choiceIndex = d.dataset.choiceIndex;
        }
        wrapper.append(label);
        d.replaceWith(wrapper);
      });
      if (!mchoices.classList.contains('mchoices')) {
        mchoices.classList.add('mchoices');
      }
      if (!serverQuiz && !preview && !mchoices.classList.contains('omchoices')) {
        mchoices.replaceChildren(...shuffled([...mchoices.children]));
      }
    }
  });
};

const makeFreeanswers = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div, q) => {
    const answer = div.querySelector('.freeanswer') as HTMLElement | null;
    if (answer) {
      const input = document.createElement('input');
      const saveNotice = document.createElement('p');
      saveNotice.classList.add('save-notice');
      saveNotice.innerText = 'Unsaved';

      input.setAttribute('type', 'text');
      input.setAttribute('placeholder', answer.innerText.trim());
      input.onchange = (e) => {
        answerChanged(q, (e.target as HTMLInputElement).value);
      };
      input.oninput = () => (saveNotice.innerText = 'Unsaved');

      answer.replaceChildren(input, saveNotice);
    }
  });
};

const makeLongFreeanswers = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div, q) => {
    const answer = div.querySelector('.longfreeanswer') as HTMLElement | null;
    if (answer) {
      const input = document.createElement('textarea');
      const saveNotice = document.createElement('p');
      saveNotice.classList.add('save-notice');
      saveNotice.innerText = 'Unsaved';

      input.setAttribute('placeholder', answer.innerText.trim());
      input.onchange = (e) => {
        answerChanged(q, (e.target as HTMLTextAreaElement).value);
      };
      input.oninput = () => (saveNotice.innerText = 'Unsaved');
      answer.replaceChildren(input, saveNotice);
    }
  });
};

/*
 * A multi-answer question (plans/done/multi-answer-questions.md): a one-line box
 * with Add beside it under a list of the answers so far; Enter or Add
 * appends the trimmed text (empty text and an exact duplicate are refused
 * with a note, the box keeping its text). Each row has edit (in place —
 * Enter / blur commits, Escape cancels, committing empty deletes the row)
 * and delete. Every change saves the WHOLE list through answerChanged, so
 * the store's buffer, retry and save notice work unchanged. The policy
 * sentence the server rendered into data-policy sits under the box.
 */
/** A quiet icon-only button (a Bootstrap Icons sprite symbol); the title
 * doubles as the accessible name. */
const iconButton = (cls: string, icon: string, title: string): HTMLButtonElement => {
  const button = document.createElement('button');
  button.type = 'button';
  button.classList.add(cls, 'icon-button');
  button.title = title;
  button.setAttribute('aria-label', title);
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.classList.add('bi');
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttributeNS(
    'http://www.w3.org/1999/xlink',
    'xlink:href',
    `/img/bootstrap-icons.svg#${icon}`,
  );
  svg.append(use);
  button.append(svg);
  return button;
};

const multianswerItem = (question: HTMLElement, value: string): HTMLLIElement => {
  const li = document.createElement('li');
  li.dataset.value = value;
  const span = $('<span>', value);
  span.classList.add('multianswer-text');
  const edit = iconButton('multianswer-edit', 'pencil', 'Change this answer');
  const del = iconButton('multianswer-delete', 'trash', 'Remove this answer');
  li.append(span, edit, del);

  const widget = must(question.querySelector('.multianswer'), 'multianswer widget');
  const commitList = () => multianswerCommit(question);
  del.onclick = () => {
    li.remove();
    commitList();
  };
  edit.onclick = () => {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = li.dataset.value ?? '';
    let done = false;
    const finish = (commit: boolean) => {
      if (done) return;
      done = true;
      const next = input.value.trim();
      if (commit && next === '') {
        li.remove();
        commitList();
        return;
      }
      if (commit && next !== li.dataset.value) {
        li.dataset.value = next;
        span.innerText = next;
        input.replaceWith(span);
        commitList();
        return;
      }
      input.replaceWith(span);
    };
    input.onkeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        finish(true);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        finish(false);
      }
    };
    input.onblur = () => finish(true);
    span.replaceWith(input);
    input.focus();
    input.select();
    widget.querySelector<HTMLElement>('.save-notice')?.replaceChildren(text('Unsaved'));
  };
  return li;
};

/** The list's values in the order written — the page shows the newest on
 * top, the stored array (and so the scorer) keeps the order they were
 * written in. */
const multianswerValues = (question: HTMLElement): string[] =>
  [...question.querySelectorAll<HTMLLIElement>('.multianswer-list li')]
    .map((li) => li.dataset.value ?? '')
    .reverse();

/** The "(N so far)" beside the heading, when there is one. */
const multianswerRefreshCount = (question: HTMLElement) => {
  const count = question.querySelector<HTMLElement>('.multianswer-count');
  if (count) count.innerText = `(${multianswerValues(question).length} so far)`;
};

const multianswerCommit = (question: HTMLElement) => {
  multianswerRefreshCount(question);
  const q = [...questions].indexOf(question);
  if (q < 0) return;
  answerChanged(q, multianswerValues(question));
};

const makeMultianswers = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div) => {
    const widget = div.querySelector('.multianswer') as HTMLElement | null;
    if (!widget) return;
    const placeholder = widget.innerText.trim() || 'Type an answer and press Enter';
    const policy = widget.dataset.policy;
    const heading = widget.dataset.heading;

    const list = document.createElement('ul');
    list.classList.add('multianswer-list');

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = placeholder;
    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.classList.add('multianswer-add');
    addButton.innerText = 'Add';
    const note = document.createElement('span');
    note.classList.add('multianswer-note');
    const entry = document.createElement('div');
    entry.classList.add('multianswer-entry');
    entry.append(input, addButton, note);

    const saveNotice = document.createElement('p');
    saveNotice.classList.add('save-notice');
    saveNotice.innerText = 'Unsaved';

    // The box sits above the list it adds to, the policy under the list.
    const children: HTMLElement[] = [entry, list];
    if (heading) {
      // Separates the student's own list from the question's body above it
      // (a vocab list's sentence frames are a list too). A real sub-head,
      // the same h3 the generated question's body uses for "Terms" and
      // "Sentence frames", so the three sections read and space alike.
      const h = document.createElement('h3');
      h.classList.add('multianswer-heading');
      const count = document.createElement('span');
      count.classList.add('multianswer-count');
      count.innerText = '(0 so far)';
      h.append(heading, ' ', count);
      children.unshift(h);
    }
    if (policy) {
      const p = $('<p>', policy);
      p.classList.add('multianswer-policy');
      children.push(p);
    }
    children.push(saveNotice);
    widget.replaceChildren(...children);

    const add = () => {
      const value = input.value.trim();
      if (!value) {
        note.innerText = '';
        return;
      }
      if (multianswerValues(div).includes(value)) {
        note.innerText = 'Already in your list';
        return;
      }
      note.innerText = '';
      list.prepend(multianswerItem(div, value));
      input.value = '';
      multianswerCommit(div);
      input.focus();
    };
    input.onkeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        add();
      }
    };
    input.oninput = () => {
      note.innerText = '';
    };
    addButton.onclick = add;
  });
};

const makeCodeanswers = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div, q) => {
    const answer = div.querySelector('.codeanswer');
    if (answer) {
      // The widget's initial text is the starter code (quiz.njk renders
      // answerSpec.starter into the container); capture it before the
      // editor replaces the children so Reset can restore it.
      const starter = (answer.textContent ?? '').trim();

      const input = document.createElement('div');
      input.classList.add('editor');

      const formatButton = document.createElement('button');
      formatButton.innerText = 'Format';

      // Flashed when Format can't parse the code; cleared after a few
      // seconds or as soon as the student edits.
      const formatNotice = document.createElement('span');
      formatNotice.classList.add('format-notice');
      let formatNoticeTimer: ReturnType<typeof setTimeout> | null = null;
      const clearFormatNotice = () => {
        if (formatNoticeTimer) clearTimeout(formatNoticeTimer);
        formatNoticeTimer = null;
        formatNotice.innerText = '';
      };
      const flashFormatNotice = () => {
        clearFormatNotice();
        formatNotice.innerText = "Can't format — check for syntax errors";
        formatNoticeTimer = setTimeout(clearFormatNotice, 4000);
      };

      // The buttons share a flex group so the status bar's space-between
      // keeps them together on the left, save notice on the right.
      const buttons = document.createElement('span');
      buttons.classList.add('buttons');
      buttons.append(formatButton);

      const saveNotice = document.createElement('span');
      saveNotice.classList.add('save-notice');
      saveNotice.innerText = 'Unsaved';

      const statusDiv = document.createElement('div');
      statusDiv.classList.add('codeStatusBar');
      statusDiv.replaceChildren(buttons, saveNotice);

      const onchange = EditorView.updateListener.of((v) => {
        if (v.docChanged) {
          saveNotice.innerText = 'Unsaved';
          clearFormatNotice();
        }
      });

      const onblur = EditorView.focusChangeEffect.of((state, focusing) => {
        if (!focusing) {
          answerChanged(q, state.doc.toString());
        }
        // No state effect to dispatch; we only want the side effects above.
        return null;
      });

      const editor = new EditorView({
        doc: (answer.textContent ?? '').trim() || EMPTY_CODE,
        extensions: [basicSetup, keymap.of([indentWithTab]), java(), onchange, onblur],
        parent: input,
      });

      formatButton.addEventListener('click', async () => {
        const formattedCode = await formatCode(editor.state.doc.toString());
        if (formattedCode === null) {
          flashFormatNotice();
        } else {
          editor.dispatch({
            changes: { from: 0, to: editor.state.doc.length, insert: formattedCode },
          });
        }
        editor.focus();
      });

      // Reset (only on questions with starter code): put the original
      // starter back, behind the in-page two-step confirm. Focusing
      // afterward means the ordinary blur-save flow persists the reset,
      // same as Format.
      if (starter) {
        const resetButton = document.createElement('button');
        resetButton.innerText = 'Reset';
        resetButton.title = 'Replace your code with the original starter code';
        confirmButton(resetButton, 'Really reset?', () => {
          editor.dispatch({
            changes: { from: 0, to: editor.state.doc.length, insert: starter },
          });
          editor.focus();
        });
        buttons.append(resetButton);
      }
      buttons.append(formatNotice);

      (answer as EditorElement)[EDITOR] = editor;
      answer.replaceChildren(input, statusDiv);
    }
  });
};

const makeRanges = (questions: HTMLCollectionOf<HTMLElement>) => {
  [...questions].forEach((div, q) => {
    const range = div.querySelector('.range') as HTMLElement | null;
    if (range) {
      const low = range.dataset.low ?? '0';
      const high = range.dataset.high ?? '100';
      const input = document.createElement('input');
      input.setAttribute('type', 'range');
      input.min = low;
      input.max = high;
      // Setting min/max clamps the browser's 0–100 default value to an end of
      // the range; start the slider at the midpoint instead. (A saved answer
      // still wins — fillRange overwrites this.)
      input.value = String(Math.round((Number(low) + Number(high)) / 2));

      // The end labels flank the slider to its left and right (a range input
      // shows nothing of its own); each falls back to the numeric bound when no
      // label is given.
      const lowLabel = $('<span>', range.dataset.lowLabel ?? low);
      lowLabel.classList.add('range-label', 'range-label-low');
      const highLabel = $('<span>', range.dataset.highLabel ?? high);
      highLabel.classList.add('range-label', 'range-label-high');

      const slider = document.createElement('div');
      slider.classList.add('range-slider');
      slider.append(lowLabel, input, highLabel);

      // A live read-out of the slider's current value.
      const value = document.createElement('output');
      value.classList.add('range-value');
      value.innerText = input.value;

      const control = document.createElement('div');
      control.classList.add('range-control');
      control.append(slider, value);

      const saveNotice = document.createElement('p');
      saveNotice.classList.add('save-notice');
      saveNotice.innerText = 'Unsaved';

      input.oninput = () => {
        value.innerText = input.value;
        saveNotice.innerText = 'Unsaved';
      };
      input.onchange = () => {
        answerChanged(q, input.value);
      };

      range.replaceChildren(control, saveNotice);
    }
  });
};

//
// Showing answers.
//

const allAnswered = (answers: LegacyAnswer[]) =>
  answers.filter((a) => a).length === questions.length;

const toggleAnswers = (key: LegacyKeyEntry[] | Record<string, KeyEntry>, show: boolean) => {
  if (show) {
    // The preview's embedded key is in the server (uuid-keyed) shape.
    if (serverQuiz || preview) {
      showServerAnswers(key as Record<string, KeyEntry>);
    } else {
      showAnswers(key as LegacyKeyEntry[]);
    }
    // The glosses just went in, long after MathJax's startup pass; a
    // question's own <m> math was typeset with the page.
    typesetMath($('#questions'));
  } else {
    hideAnswers();
  }
};

const makeGloss = (explanation: string) => {
  const gloss = document.createElement('p');
  gloss.classList.add('gloss');
  gloss.innerHTML = explanation;
  return gloss;
};

const showAnswers = (key: LegacyKeyEntry[]) => {
  const questions = $('#questions').children;
  [...questions].forEach((q, i) => {
    const { answer, explanation } = key[i];
    const gloss = explanation ? makeGloss(explanation) : undefined;
    showChoices(q, answer, gloss);
    showMultipleChoices(q, answer, gloss);
    showFreeanswer(q, answer, gloss);
    showCodeanswer(q, answer, gloss);
  });
};

/*
 * Server-mode key display: uuid-keyed entries whose correct choices are
 * identified by display index (data-choice-index) rather than text.
 */
const showServerAnswers = (key: Record<string, KeyEntry>) => {
  ([...questions] as HTMLElement[]).forEach((q) => {
    const uuid = q.dataset.questionUuid;
    const entry = uuid ? key[uuid] : undefined;
    if (!entry) return;
    const { answer, explanation, rubric } = entry;
    const gloss = explanation ? makeGloss(explanation) : undefined;
    if (rubric?.length) {
      showRubric(q, rubric, gloss);
      return;
    }
    // The casts encode which AnswerValue shape each form's key entry holds: a
    // single ChoiceAnswer for choices, a ChoiceAnswer[] for mchoices, a
    // string otherwise.
    switch (q.dataset.form) {
      case 'choices':
        markCorrectByIndex(q, '.choices, .ochoices', answer ? [answer as ChoiceAnswer] : [], gloss);
        break;
      case 'mchoices':
        markCorrectByIndex(q, '.mchoices, .omchoices', (answer ?? []) as ChoiceAnswer[], gloss);
        break;
      case 'tf':
      case 'yn':
        markCorrectByText(q, answer as string, gloss);
        break;
      case 'freeanswer':
      case 'longfreeanswer':
        showFreeanswer(q, answer, gloss, '.freeanswer, .longfreeanswer');
        break;
      // A keyed multi-answer's key is its listed accepted answers, shown as
      // a free answer's are; a keyless one has no key (hasKey says so).
      case 'multianswer':
        showFreeanswer(q, answer, gloss, '.multianswer');
        break;
      case 'codeanswer':
        showCodeanswer(q, answer, gloss);
        break;
      case 'range':
        showRange(q, answer, gloss);
        break;
    }
  });
};

// A rubric question's key (plans/rubric-questions.md): the rubric the
// answer was graded against, in place of the free forms' accepted answers.
// Points show only when the items aren't all worth the same.
const showRubric = (
  q: Element,
  rubric: { html: string; points: number }[],
  gloss?: HTMLElement,
) => {
  const widget = q.querySelector('.freeanswer, .longfreeanswer, .codeanswer');
  if (!widget) return;
  const div = document.createElement('div');
  div.classList.add('canonical', 'rubric-key');
  const p = document.createElement('p');
  p.append($('<b>', 'A complete answer includes:'));
  div.append(p);
  const weighted = rubric.some((r) => r.points !== rubric[0].points);
  const ul = document.createElement('ul');
  for (const r of rubric) {
    const li = document.createElement('li');
    li.innerHTML = r.html;
    if (weighted) li.append(` (${r.points} ${r.points === 1 ? 'point' : 'points'})`);
    ul.append(li);
  }
  div.append(ul);
  // Where the free forms put their canonical answer: before the save
  // notice, or after codeanswer's status bar (its notice lives inside it).
  const bar = widget.querySelector('.codeStatusBar');
  if (bar) bar.after(div);
  else must(widget.querySelector('.save-notice'), 'save notice in answer widget').before(div);
  if (gloss) div.after(gloss);
};

// A range's key is the expected number (or empty for an opinion slider); show
// it beside the slider the way freeanswer shows its canonical answer.
const showRange = (q: Element, answer: LegacyAnswer | AnswerValue, gloss?: HTMLElement) => {
  const range = q.querySelector('.range');
  if (!range) return;
  const values = (answer ?? []) as string[];
  if (values.length && values[0]) {
    const div = document.createElement('div');
    div.classList.add('canonical');
    const p = document.createElement('p');
    p.append($('<b>', 'Correct value: '));
    p.append(values[0]);
    div.append(p);
    must(range.querySelector('.save-notice'), 'save notice in range').before(div);
    if (gloss) div.after(gloss);
  } else if (gloss) {
    range.append(gloss);
  }
};

const markCorrectByIndex = (
  question: HTMLElement,
  selector: string,
  correct: ChoiceAnswer[],
  gloss?: HTMLElement,
) => {
  const container = question.querySelector(selector);
  if (!container) return;
  const indexes = new Set(correct.map((c) => c.index));
  ([...container.children] as HTMLElement[]).forEach((wrapper) => {
    if (indexes.has(Number(wrapper.dataset.choiceIndex))) {
      wrapper.classList.add('correct');
    }
  });
  if (gloss) {
    container.after(gloss);
  }
};

const markCorrectByText = (question: HTMLElement, answer: string, gloss?: HTMLElement) => {
  const container = question.querySelector('.tf, .yn');
  if (!container) return;
  ([...container.children] as HTMLElement[]).forEach((p) => {
    if (cleanAnswer(p) === answer) {
      p.classList.add('correct');
    }
  });
  if (gloss) {
    container.after(gloss);
  }
};

const cleanAnswer = (p: HTMLElement) => {
  return p.innerText
    .trim()
    .replaceAll(/<\/?\w+?>/g, '')
    .replaceAll(/\s+/gs, ' ');
};

const showChoices = (q: Element, answer: LegacyAnswer, gloss?: HTMLElement) => {
  const choices = q.querySelector('.choices');
  if (choices) {
    ([...choices.children] as HTMLElement[]).forEach((p) => {
      if (p) {
        if (cleanAnswer(p) === answer) {
          p.classList.add('correct');
        }
      }
    });
    if (gloss) {
      choices.after(gloss);
    }
  }
};

const showMultipleChoices = (q: Element, answer: LegacyAnswer, gloss?: HTMLElement) => {
  const mchoices = q.querySelector('.mchoices');
  if (mchoices) {
    ([...mchoices.children] as HTMLElement[]).forEach((p) => {
      if (p) {
        if (answer.includes(cleanAnswer(p))) {
          p.classList.add('correct');
        }
      }
    });
    if (gloss) {
      mchoices.after(gloss);
    }
  }
};

// A free-answer key is the array of acceptable answers, canonical first:
// show the canonical one alone, or the whole list when there are several.
const showFreeanswer = (
  q: Element,
  answer: LegacyAnswer | AnswerValue,
  gloss?: HTMLElement,
  selector = '.freeanswer',
) => {
  const freeanswer = q.querySelector(selector);
  if (freeanswer) {
    // Server keys are always arrays; a legacy key.json entry may be a bare
    // string.
    const answers = (Array.isArray(answer) ? answer : answer == null ? [] : [answer]) as string[];
    const div = document.createElement('div');
    div.classList.add('canonical');
    if (answers.length > 1) {
      const p = document.createElement('p');
      p.append($('<b>', 'Accepted answers:'));
      div.append(p);
      const ul = document.createElement('ul');
      for (const a of answers) {
        const li = document.createElement('li');
        li.append(a);
        ul.append(li);
      }
      div.append(ul);
    } else {
      const p = document.createElement('p');
      p.append($('<b>', 'Canonical answer: '));
      p.append(answers[0] ?? '');
      div.append(p);
    }
    must(freeanswer.querySelector('.save-notice'), 'save notice in freeanswer').before(div);
    if (gloss) {
      div.after(gloss);
    }
  }
};

const showCodeanswer = (q: Element, answer: LegacyAnswer | AnswerValue, gloss?: HTMLElement) => {
  const a = q.querySelector('.codeanswer');
  if (a) {
    const div = document.createElement('div');
    div.classList.add('canonical');
    const p = document.createElement('p');
    p.append(`Canonical answer:`);
    div.append(p);
    const pre = document.createElement('pre');
    pre.setAttribute('class', 'language-java');
    const code = document.createElement('code');
    code.dataset.trim = '';
    code.dataset.noescape = '';
    code.setAttribute('class', 'language-java');
    code.append((answer as string[])[0].trimEnd());
    pre.append(code);
    div.append(pre);
    Prism.highlightElement(pre);
    // Unlike the other widgets' save notices, codeanswer's lives INSIDE the
    // .codeStatusBar flex row — inserting there would make the canonical
    // answer and gloss flex items beside the Format button. Insert after
    // the status bar, in the widget's normal flow.
    must(a.querySelector('.codeStatusBar'), 'status bar in codeanswer').after(div);
    if (gloss) {
      div.after(gloss);
    }
  }
};

const hideAnswers = () => {
  document.querySelectorAll('.correct').forEach((e) => e.classList.remove('correct'));
  document.querySelectorAll('.canonical').forEach((e) => e.remove());
  document.querySelectorAll('.gloss').forEach((e) => e.remove());
};

const loadKey = async (storage: Storage, answers: LegacyAnswer[] | undefined) => {
  const show = $('#show-answers');

  try {
    key = JSON.parse(await storage.loadFromWeb('key.json')) as LegacyKeyEntry[];
    if (answers && allAnswered(answers)) {
      revealShowAnswersToggle();
    } else {
      show.style.display = 'none';
    }
  } catch {
    console.log('key.json not found');
    show.style.display = 'none';
  }
};

const saveAnswer = (q: number, answer: LegacyAnswer) => {
  answers[q] = answer;
  void saveAnswers(questions[q]);
};

/*
 * Legacy mode persists the whole positional answers.json on every change.
 * The changed question's notice reports the real outcome — a notice may
 * say "Saved" only once the storage layer has said so (a buffered save
 * says it's retrying; browser-only storage never claims to have saved).
 */
const saveAnswers = async (question: HTMLElement) => {
  const notice = saveNoticeFor(question);
  if (legacyLoadFailed || !storage?.saves) {
    notice.innerText = 'Not saved';
    return;
  }
  notice.innerText = 'Saving…';
  try {
    const result = await storage.save('answers.json', JSON.stringify(answers, null, 2));
    if (result.buffered) {
      notice.innerText = result.authLost ? 'Not saved — logged out' : 'Offline — retrying';
    } else {
      notice.innerText = 'Saved';
    }
  } catch (e) {
    notice.innerText = 'Not saved';
    pageError(describe(e, 'save your answer'));
  }
};

const show = (n: number) => {
  $('#q').innerHTML = `${n + 1}`;
  window.location.hash = `#${n + 1}`;
  questions[current].style.display = 'none';
  current = n;
  questions[current].style.display = 'block';
};

const previous = (e: MouseEvent) => {
  show((current - 1 + questions.length) % questions.length);
  e.stopPropagation();
  e.preventDefault();
};

const next = (e: MouseEvent) => {
  show((current + 1) % questions.length);
  e.stopPropagation();
  e.preventDefault();
};

const hideInstructions = () => {
  hidden.style.display = 'inline';
  instructions.style.display = 'none';
};

const showInstructions = () => {
  hidden.style.display = 'none';
  instructions.style.display = 'inline';
};

setup().catch((e) =>
  bootstrapFailed(e, () => {
    // A quiz that can't save must not look like one that can.
    questionsElement.inert = true;
  }),
);
